import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydSelect from '../../../../commonDesign/SydSelect'
import JsonField from './JsonField'
import MetadataRowsEditor from './MetadataRowsEditor'
import StatusStyleOverridesEditor from './StatusStyleOverridesEditor'
import { AGGREGATION_LEVEL_VALUES, DEFAULT_AGGREGATION_LEVELS, DEFAULT_ITEM_METADATA } from './constants'
import { mergeMetadataScopes, splitMetadataScopes } from './metadataScope'
import { statusStylesObjectToRows, statusStylesRowsToObject } from './statusStyleHelpers'

// Bridges the persisted object-map shape of `statusStyleOverrides` to the
// structured editor's row array. Local state owns the row identities
// (`_rowId`) so DnD keys stay stable while the admin edits. We re-sync from
// the form value when it changes externally (e.g. a form reset after save),
// but skip the re-sync we trigger ourselves via onChange — otherwise every
// keystroke would regenerate row ids and break in-flight focus / drags.
function StatusStyleOverridesField ({ value, onChange }) {
  const [rows, setRows] = useState(() => statusStylesObjectToRows(value))
  const isLocalChangeRef = useRef(false)

  useEffect(() => {
    if (isLocalChangeRef.current) {
      isLocalChangeRef.current = false
      return
    }
    setRows(statusStylesObjectToRows(value))
  }, [value])

  const handleRowsChange = useCallback((nextRows) => {
    isLocalChangeRef.current = true
    setRows(nextRows)
    onChange(statusStylesRowsToObject(nextRows))
  }, [onChange])

  return <StatusStyleOverridesEditor value={rows} onChange={handleRowsChange} />
}

StatusStyleOverridesField.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

const AGGREGATION_LEVEL_OPTIONS = [
  { label: 'Categories', value: 'categories' },
  { label: 'Items', value: 'items' }
]

// Normalize the persisted value for the SydSelect: null/undefined or stale
// entries fall back to the default (both levels). The renderer already treats
// null/empty as "both" via resolveActiveLevels, so the UI mirrors that so
// admins see the effective state instead of an empty dropdown.
function normalizeAggregationLevels (current) {
  const baseline = Array.isArray(current) ? current : null
  const filtered = baseline?.filter(value => AGGREGATION_LEVEL_VALUES.includes(value)) ?? []
  return filtered.length ? filtered : DEFAULT_AGGREGATION_LEVELS
}

function SalesforceStatusReportConfigEditor ({ fieldPrefix }) {
  const { control, setValue } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  // The two underlying arrays are still persisted independently so the
  // renderer's existing contract (and ordering flexibility per scope) is
  // preserved. The editor reads both, merges them into a unified list with a
  // per-row scope, and writes the split back on every change.
  const itemMetadata = useWatch({ control, name: `${configPrefix}.itemMetadata` })
  const categoryMetadata = useWatch({ control, name: `${configPrefix}.categoryMetadata` })

  // Drives the disabled / cleared state of the status-icon toggle: the icon
  // lives inside the title row, so hiding the title implicitly hides the icon
  // regardless of whether the underlying value is true.
  const showPlanCard = useWatch({ control, name: `${configPrefix}.showPlanCard` })
  const planCardHeaderVisible = showPlanCard !== false

  const unifiedRows = useMemo(
    () => mergeMetadataScopes(itemMetadata, categoryMetadata),
    [itemMetadata, categoryMetadata]
  )

  const handleUnifiedChange = useCallback((nextRows) => {
    const { itemMetadata: nextItems, categoryMetadata: nextCategories } = splitMetadataScopes(nextRows)
    setValue(`${configPrefix}.itemMetadata`, nextItems, { shouldDirty: true, shouldTouch: true })
    setValue(`${configPrefix}.categoryMetadata`, nextCategories, { shouldDirty: true, shouldTouch: true })
  }, [configPrefix, setValue])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Smart Endpoint Config</div>
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.endpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Endpoint' description='Smart endpoint base name'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='sf-status-report'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.policy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Policy' description='Production policy'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wealthOwners'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.tag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Tag' description='Production tag'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='live'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4} className='flex items-center'>
        <Controller
          name={`${configPrefix}.showDebugControl`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show debug control for Summit users (shown by default)'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.devPolicy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Policy' description='Debug policy (defaults to Policy)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Policy'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.devTag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Tag' description='Debug tag (defaults to Tag)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Tag'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' description='Defaults to the plan name'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Plan name from response'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.allItemsLabel`}
          control={control}
          render={({ field }) => (
            <SydLabel label='All Items Label' description='Label for the first filter pill'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='All Statuses'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.panelWidth`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Detail Panel Width' description='Side panel width in pixels (default 600)'>
              <SydInput
                type='number'
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value
                  if (!raw || !raw.trim()) { field.onChange(null); return }
                  const parsed = parseInt(raw, 10)
                  field.onChange(isNaN(parsed) ? null : parsed)
                }}
                placeholder='600'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Plan Card</div>
      </Grid>

      <Grid item xs={4} className='flex items-center'>
        <Controller
          name={`${configPrefix}.showPlanCard`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show the "Plan" title row inside the card (shown by default)'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => {
                field.onChange(checked)
                // When the title row is hidden the status icon has nowhere
                // to render, so we auto-clear the dependent toggle to keep
                // the saved config honest about what will actually display.
                if (!checked) {
                  setValue(`${configPrefix}.showPlanCardStatus`, false, { shouldDirty: true, shouldTouch: true })
                }
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.planCardLabel`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Plan Card Title'
              description={planCardHeaderVisible ? 'Heading shown in the title row' : 'Disabled — title row is hidden'}
            >
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='The Plan'
                disabled={!planCardHeaderVisible}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4} className='flex items-center'>
        <Controller
          name={`${configPrefix}.showPlanCardStatus`}
          control={control}
          render={({ field }) => {
            const planCardEnabled = planCardHeaderVisible
            return (
              <SydCheckbox
                label='Show the plan status icon next to the title (only when the title row is shown)'
                size='small'
                disabled={!planCardEnabled}
                value={planCardEnabled && field.value !== false}
                onChange={(_e, checked) => field.onChange(checked)}
              />
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Filter Aggregation</div>
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.aggregationLevels`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Aggregation Levels'
              description='Levels that feed the filter pills. Leave empty to include both.'
            >
              <SydSelect
                multiple
                size='sm'
                minWidth='0'
                placeholder='Select levels...'
                value={normalizeAggregationLevels(field.value)}
                onChange={(e) => {
                  const next = Array.isArray(e.target.value) ? e.target.value : []
                  field.onChange(AGGREGATION_LEVEL_VALUES.filter(value => next.includes(value)))
                }}
                options={AGGREGATION_LEVEL_OPTIONS}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Status Styling</div>
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.statusStyleOverrides`}
          control={control}
          render={({ field }) => (
            <SydLabel
              description='Customize each status. Blank fields fall back to the built-in defaults (On Track, In Progress, Not Started, Complete, Not Now).'
            >
              <StatusStyleOverridesField
                value={field.value}
                onChange={field.onChange}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Detail Panel Rows</div>
      </Grid>

      <Grid item xs={12}>
        <SydLabel
          description='Rows shown in the detail panel. "Show On" picks which panel(s) each row appears in. Click Restore Defaults for the four item starter rows (Status, Advisor Notes, Next Review Date, Last Reviewed Date).'
        >
          <MetadataRowsEditor
            value={unifiedRows}
            onChange={handleUnifiedChange}
            defaults={DEFAULT_ITEM_METADATA}
            showRestoreDefaults
            emptyMessage='No rows configured — both panels will render empty. Click Add Row or Restore Defaults to start.'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Embedded Tasks Component</div>
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.taskComponent.blueprintCode`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Blueprint Code' description='Blueprint to embed (e.g. wo-sf-task-list)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wo-sf-task-list'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.taskComponent.presentationKey`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Presentation Key' description='Presentation within the blueprint'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='wo-sf-task-list-compact'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${configPrefix}.taskComponent.styleVariant`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Style Variant' description='Variant override (e.g. sm, md, lg)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='sm'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.taskComponent.configOverrides`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Config Overrides'
              description='Merged into the embedded component config. `filterIds` is injected from the selected plan item.'
            >
              <JsonField
                value={field.value}
                onChange={field.onChange}
                placeholder='{ "title": "Action Items" }'
                rows={4}
                expectObject
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.taskComponent.containerStyle`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Container Style' description='Style for the embedded component wrapper'>
              <JsonField
                value={field.value}
                onChange={field.onChange}
                placeholder='{ "margin": "-8px -12px" }'
                rows={3}
                expectObject
              />
            </SydLabel>
          )}
        />
      </Grid>

    </Grid>
  )
}

SalesforceStatusReportConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default SalesforceStatusReportConfigEditor
